/**
 * BulkSelectToolbar — shared bulk-action UI for list pages.
 *
 * Renders a small sticky toolbar showing the selected count + a
 * "Select all visible" / "Clear" / "Delete N" trio. Opens a single
 * confirmation modal before firing the action.
 *
 * Used by BookingsHub: Bookings tab, Links tab, Appointment Types tab,
 * Forms tab.
 */
import { useCallback, useMemo, useState } from "react";
import { CheckSquare, Square, Trash2, X as XIcon, AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export function BulkSelectToolbar({
  selectedCount,
  totalVisible,
  onSelectAll,
  onClear,
  onDelete,
  itemLabel = "item",
  itemLabelPlural,
  busy = false,
  testidPrefix = "bulk",
  // Optional override for the destructive-action button label.
  // Defaults to "Delete" so existing callers (Links, Appointment
  // Types, Forms) keep their current copy. The bookings panel passes
  // "Archive" / "Restore" depending on its active tab.
  actionLabel = "Delete",
  actionTone = "destructive",
}) {
  const plural = itemLabelPlural || `${itemLabel}s`;
  const allSelected = selectedCount === totalVisible && totalVisible > 0;
  if (selectedCount === 0) {
    // When nothing selected, just show a "Select all visible" affordance.
    return (
      <div className="flex items-center gap-3 text-xs text-[#82828c] mb-3">
        <button
          type="button"
          onClick={onSelectAll}
          className="inline-flex items-center gap-1.5 hover:text-[#1a1a1f]"
          data-testid={`${testidPrefix}-select-all`}
          disabled={totalVisible === 0}
        >
          <Square className="w-3.5 h-3.5" /> Select all {totalVisible > 0 ? `(${totalVisible})` : ""}
        </button>
      </div>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-3 mb-3 px-3 py-2 rounded-md bg-[#005e7a] text-white"
      data-testid={`${testidPrefix}-toolbar`}
    >
      <span className="text-xs font-medium" data-testid={`${testidPrefix}-selected-count`}>
        {selectedCount} selected
      </span>
      <span className="flex-1" />
      <button
        type="button"
        onClick={onSelectAll}
        className="text-xs px-2.5 py-1 rounded hover:bg-white/10 inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-toggle-all`}
      >
        {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
        {allSelected ? "All selected" : `Select all (${totalVisible})`}
      </button>
      <button
        type="button"
        onClick={onClear}
        className="text-xs px-2.5 py-1 rounded hover:bg-white/10 inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-clear`}
      >
        <XIcon className="w-3.5 h-3.5" /> Clear
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5 font-medium ${
          actionTone === "neutral"
            ? "bg-white text-[#005e7a] hover:bg-white/90"
            : "bg-[#c43838] hover:bg-[#a82929]"
        }`}
        data-testid={`${testidPrefix}-delete`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        {actionLabel} {selectedCount} {selectedCount === 1 ? itemLabel : plural}
      </button>
    </div>
  );
}

export function BulkDeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  count,
  itemLabel,
  itemLabelPlural,
  description,
  busy = false,
  testidPrefix = "bulk-delete",
  // Same opt-in override pattern as BulkSelectToolbar.
  actionLabel = "Delete",
  actionTone = "destructive",
}) {
  const plural = itemLabelPlural || `${itemLabel}s`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid={`${testidPrefix}-confirm-dialog`}>
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 ${
              actionTone === "neutral" ? "text-[#005e7a]" : "text-[#c43838]"
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            {actionLabel} {count} {count === 1 ? itemLabel : plural}?
          </DialogTitle>
          <DialogDescription className="text-[#4a4a52]">
            {description || `This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="bloq-btn-secondary"
            data-testid={`${testidPrefix}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`bloq-btn-primary ${
              actionTone === "neutral" ? "" : "bg-[#c43838] hover:bg-[#a82929]"
            }`}
            data-testid={`${testidPrefix}-submit`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>{actionLabel} {count} {count === 1 ? itemLabel : plural}</>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * useBulkSelect — small hook for managing selection state on list pages.
 * Returns: { selected, isSelected, toggle, selectAll, clear, count, ids }
 *
 * Pass the current visible list of IDs to selectAll so it only ever
 * adds rows the user can actually see (e.g. after a filter is applied).
 * If every visible row is already selected, selectAll toggles them off.
 */
export function useBulkSelect() {
  const [selected, setSelected] = useState(() => new Set());
  const isSelected = useCallback((id) => selected.has(id), [selected]);
  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAll = useCallback((ids) => {
    setSelected((prev) => {
      const allIn = ids.every((id) => prev.has(id));
      if (allIn) {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);
  const clear = useCallback(() => setSelected(new Set()), []);
  const count = useMemo(() => selected.size, [selected]);
  const ids = useMemo(() => Array.from(selected), [selected]);
  return { selected, isSelected, toggle, selectAll, clear, count, ids };
}
